import { useMemo } from "react";
import { TFunction } from "i18next";
import {
  TaskExecution,
  TaskStandAloneAlarm,
  useGetTasksAlarmStandAloneQuery,
  useGetTasksExecutionQuery,
} from "shared/api";
import { getUserDisplayName } from "shared/adapters";
import {
  CalendarEvent,
  CalendarMode,
  DateRange,
  TaskCalendarStatus,
} from "../types";

interface UseTaskCalendarEventsParams {
  mode: CalendarMode;
  range: DateRange;
  t: TFunction;
}

export const useTaskCalendarEvents = ({
  mode,
  range,
  t,
}: UseTaskCalendarEventsParams) => {
  const executionArgs = {
    timestamp_expected__gte: range.start,
    timestamp_expected__lte: range.end,
    expand: "task,user",
  };

  const { data: executionTasks } = useGetTasksExecutionQuery(executionArgs, {
    skip: mode !== "executions" || !range.start || !range.end,
    refetchOnMountOrArgChange: true,
  });

  const alarmArgs = {
    timestamp_triggered__lte: range.end,
    timestamp_updated__gte: range.start,
    expand: "task_standalone",
  };

  const { data: alarmTasks } = useGetTasksAlarmStandAloneQuery(alarmArgs, {
    skip: mode !== "standalone-alarms" || !range.start || !range.end,
    refetchOnMountOrArgChange: true,
  });

  const events = useMemo((): CalendarEvent[] => {
    if (mode === "executions") {
      return (
        executionTasks?.results?.map((task: TaskExecution) => {
          const start = new Date(task.timestamp_expected);
          const end = new Date(start);
          end.setHours(start.getHours() + 1);

          return {
            id: String(task.id),
            title:
              task.task?.name ??
              (t("taskHub.schedule.unnamedTask") as string) ??
              "Unnamed Task",
            start: start.toISOString(),
            end: end.toISOString(),
            extendedProps: {
              status: task.status as TaskCalendarStatus,
              assignee:
                task.user && typeof task.user === "object"
                  ? getUserDisplayName(task.user)
                  : t("taskHub.schedule.unassigned") || "Unassigned",
            },
          };
        }) ?? []
      );
    }

    return (
      alarmTasks?.map((alarm: TaskStandAloneAlarm) => {
        const start = new Date(alarm.timestamp_triggered);
        const end = new Date(alarm.timestamp_updated);

        return {
          id: String(alarm.id),
          title: alarm.task_standalone?.name ?? "Standalone Alarm",
          start: start.toISOString(),
          end: end.toISOString(),
          extendedProps: {
            status: alarm.is_active
              ? TaskCalendarStatus.ACTIVE
              : TaskCalendarStatus.INACTIVE,
          },
        };
      }) ?? []
    );
  }, [mode, executionTasks, alarmTasks, t]);

  return { events };
};
